import type { ArabicCatalog } from "@ncp/i18n";
import type en from "./requests.en";

/** الطلبات الموجهة إلى جهات خارج الملف: بيانات دراسات السوق من المنشآت، والآراء الفنية من الجهات التنظيمية (G2G). */
const ar: ArabicCatalog<typeof en> = {
  title: "طلبات البيانات والآراء الفنية",
  subtitle: "ما طلبته الإدارة من المنشآت لدراسات السوق ومن الجهات التنظيمية من آراء فنية، وموقف الرد على كل طلب.",
  kind: { MarketData: "بيانات دراسة سوق", TechnicalOpinion: "رأي فني" },
  kindLong: { MarketData: "طلب بيانات لدراسة سوق ({{marketDataBasis}})", TechnicalOpinion: "طلب رأي فني ({{opinionBasis}})" },
  status: {
    Sent: "مرسل",
    Opened: "فتحه المرسل إليه",
    Submitted: "ورد الرد",
    ClarificationRequested: "طلب إيضاح",
    Completed: "مغلق",
    Withdrawn: "مسحوب",
    Lapsed: "انقضت المهلة",
  },
  completed: { MarketData: "مقبول", TechnicalOpinion: "تمت دراسته" },
  filters: { all: "الكل", open: "المفتوحة", kind: "النوع" , withClosed: "بما فيها المغلقة" },
  columns: { reference: "المرجع", addressee: "الجهة المرسل إليها", subject: "الموضوع", due: "موعد الرد", status: "الحالة", about: "بشأن" },
  overdue: "متأخر",
  empty: "لا توجد طلبات بعد",
  emptyHint: "ترسل طلبات البيانات من دراسة السوق في بنك البيانات، وطلبات الرأي الفني من ملف الحالة.",
  study: {
    title: "طلبات البيانات من المنشآت",
    hint: "ترد كل منشأة عبر البوابة بأرقامها ومستنداتها المعززة ({{marketDataBasis}}). وتحفظ أسطر المبيعات المقبولة في بنك البيانات بوصفها إفصاحا ذاتيا من المنشأة.",
    new: "طلب بيانات",
    empty: "لم تطلب بيانات لهذه الدراسة بعد.",
    file: "ملف دراسة السوق:",
    testFile: "ملف اختبار: لا تصل طلباته إلى أي جهة خارج الإدارة",
    noFile: "غير مرتبطة بملف دراسة سوق: تصل طلباتها إلى المنشآت بإشعار في البوابة وبالبريد الإلكتروني. ويربط الملف قبل أول طلب حتى لا تصل الطلبات إلى أحد إذا كان ملف اختبار.",
    noFileSent: "غير مرتبطة بملف دراسة سوق، وقد أرسلت الطلبات بالفعل.",
    tie: "ربط بملف دراسة سوق",
    tieConfirm: "ربط الدراسة",
    fileTied: "ربطت الدراسة بملف دراسة السوق.",
  },
  opinions: {
    tab: "الآراء الفنية",
    title: "الآراء الفنية",
    lead: "آراء الجهات التنظيمية القطاعية والجهات المحلية المعنية في هذا الملف ({{opinionBasis}}).",
    hint: "طلب رأي جهة تنظيمية قطاعية أو جهة محلية معنية ({{opinionBasis}} – الرد خلال {{opinionPeriod}}). وفي عمليات التركز يوقف الطلب المدة القانونية حتى ورود الرأي (المادة 14(1)(ب)).",
    new: "طلب رأي فني",
    askThis: "طلب رأيها الفني",
    empty: "لم يطلب رأي فني في هذا الملف.",
    linked: "{{reference}} · {{status}}",
  },
  newData: {
    title: "طلب بيانات لدراسة السوق",
    description: "طلب لكل منشأة بمرجع مستقل. وتبلغ المنشأة عبر البوابة وبالبريد الإلكتروني.",
    recipients: "المنشآت",
    fromMarket: "في هذه السوق",
    noMarketEntities: "لا توجد في سوق الدراسة منشآت لها حصص بعد. ابحثوا في بنك البيانات أو أضيفوا منشأة بالاسم.",
    search: "البحث في بنك البيانات",
    searchPlaceholder: "الاسم أو رقم الرخصة أو رمز LEI",
    addOther: "إضافة منشأة غير موجودة في بنك البيانات",
    otherName: "اسم المنشأة",
    contactName: "مسؤول الاتصال",
    contactEmail: "بريد مسؤول الاتصال",
    contactEmailHint: "مطلوب إذا لم يكن للمنشأة حساب على البوابة بعد.",
    portalAccount: "مسجلة على البوابة",
    noPortalAccount: "لا حساب على البوابة",
    remove: "حذف",
    subject: "الموضوع",
    purpose: "الغرض من البيانات",
    purposeHint: "يظهر للمنشأة: الدراسة وسبب الحاجة إلى أرقامها.",
    items: "المطلوب",
    itemKind: { Text: "رد مكتوب", Number: "رقم", Sales: "المبيعات حسب خط المنتج", Document: "مستندات" },
    required: "مطلوب",
    addQuestion: "إضافة سؤال",
    years: "السنوات",
    dueOn: "موعد الرد",
    dueHint: "{{days}} يوم عمل ما لم تحددوا موعدا آخر.",
    send: "إرسال الطلبات ({{count}})",
    sent: "تم إرسال الطلبات: {{count}}",
  },
  newOpinion: {
    title: "طلب رأي فني",
    description: "{{contentBasis}}: يكون الطلب مكتوبا ويتضمن ملخص الملف والممارسات والأسئلة، ويعامل بسرية.",
    body: "الجهة التنظيمية أو المعنية",
    suggested: "مقترحة لأسواق هذا الملف",
    regulators: "الجهات التنظيمية القطاعية",
    authorities: "الجهات المحلية المعنية",
    coordinationEntry: "سجل التنسيق",
    newEntry: "إضافة سجل جديد",
    focalName: "ضابط الاتصال",
    focalEmail: "بريد ضابط الاتصال",
    focalEmailHint: "يرسل الطلب إلى هذا العنوان، ومن يسجل الدخول به يرد عليه. ويحفظ للطلبات اللاحقة.",
    subject: "الموضوع",
    summary: "ملخص الملف",
    context: "الممارسات أو العملية أو الطلب محل الرأي",
    questions: "الأسئلة",
    addQuestion: "إضافة سؤال",
    dueOn: "موعد الرد {{date}} ({{opinionPeriod}}، {{opinionBasis}})",
    send: "إرسال الطلب",
    sent: "تم إرسال الطلب",
    interruptsClock: "يوقف إرسال الطلب المدة القانونية حتى ورود الرأي.",
  },
  review: {
    request: "الطلب",
    answer: "الرد",
    noAnswer: "لم يرد الرد بعد.",
    earlierRounds: "الردود السابقة كما قدمت",
    round: "الجولة {{n}} · قدمت في {{date}}",
    files: "الملفات",
    ministryFile: "أرفقته الإدارة",
    recipientFile: "من الجهة المرسل إليها",
    attach: "إرفاق مستند للجهة المرسل إليها",
    attached: "تم إرفاق المستند",
    history: "السجل",
    respondent: "قدم الرد {{name}}",
    confidential: "سري",
    confidentialityNote: "السرية",
    salesFiled: "أسطر المبيعات المحفوظة في بنك البيانات: {{count}}",
    testFile: "ملف اختبار: لا يصل إلى أي جهة خارج الإدارة",
    crossCheckTitle: "أرقام لا تؤيدها مصادر بنك البيانات الأخرى: {{count}}",
    crossCheckHint: "يراجع كل رقم مع المصدر المذكور قبل القبول، إذ يحفظ الرد بوصفه إفصاحا ذاتيا من المنشأة. ويمكن طلب إيضاح، أو القبول مع ذكر الأسباب.",
    crossCheckReasons: "أسباب قبول الأرقام التي لا يؤيدها بنك البيانات",
    positions: {
      NoConcerns: "لا توجد ملاحظات تنظيمية",
      ConcernsAddressable: "ملاحظات يمكن معالجتها بشروط",
      SeriousConcerns: "ملاحظات جوهرية",
      OutsideRemit: "خارج اختصاص الجهة",
    },
    position: "الموقف",
    statement: { MarketData: "ملاحظات على الأرقام", TechnicalOpinion: "الرأي الفني" },
    marketAssessment: "حالة المنافسة",
    conditions: "الشروط المقترحة",
    sources: "المصادر",
    contact: "موجه إلى",
    issuedBy: "أرسله {{name}}",
    accept: { MarketData: "قبول وحفظ في بنك البيانات", TechnicalOpinion: "تسجيل دراسة الرأي" },
    acceptNote: "ملاحظة للملف (اختيارية)",
    accepted: { MarketData: "تم القبول – أسطر المبيعات المحفوظة: {{count}}", TechnicalOpinion: "سجلت دراسة الرأي" },
    clarify: "طلب إيضاح",
    clarifyNote: "ما المطلوب إيضاحه",
    clarifyDue: "الموعد الجديد",
    clarified: "تم طلب الإيضاح",
    withdraw: "سحب الطلب",
    withdrawReason: "السبب",
    withdrawn: "تم سحب الطلب",
    lapse: "إغلاق دون رد",
    lapseNote: "ملاحظة",
    lapsed: "تم إغلاق الطلب",
    extension: "تطلب {{name}} مهلة حتى {{date}}",
    grant: "منح",
    refuse: "رفض",
    granted: "منحت المهلة",
    refused: "رفضت المهلة",
    send: "إرسال",
  },
  events: {
    Sent: "أرسل",
    Opened: "فتحه المرسل إليه",
    Submitted: "قدم الرد",
    ClarificationRequested: "طلب إيضاح",
    ExtensionRequested: "طلب مهلة",
    ExtensionGranted: "منح مهلة",
    ExtensionRefused: "رفض المهلة",
    Reminder: "أرسل تذكير",
    Completed: "أغلق",
    Withdrawn: "سحب",
    Lapsed: "أغلق دون رد",
  },
  itemKinds: { Text: "رد مكتوب", Number: "رقم", Sales: "جدول مبيعات", Document: "مستندات" },
  sales: { productLine: "خط المنتج", hsCode: "النظام المنسق", year: "السنة", flow: "النوع", value: "القيمة (درهم)", volume: "الكمية", flows: { Domestic: "داخل الدولة", Import: "واردات", Export: "صادرات" } },
};

export default ar;
